// app/fragments.jsx — extracted from App.js (behaviour-preserving split).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import QRCode from "qrcode";
import {
  Bell,
  CheckCircle2,
  Copy,
  Download,
  Radio,
  RefreshCw,
  ScanLine,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/sonner";
import { describeBrowserSupport, getNfcPermissionState, isNfcSupported, writeNfcUrl } from "@/lib/nfc";
import { formatPrice, getCartKey } from "@/app/core";


export const saveCart = (tagCode, cart) => {
  if (!tagCode) return;
  window.localStorage.setItem(getCartKey(tagCode), JSON.stringify(cart));
};

const statusClassName = {
  pending: "border-transparent bg-secondary text-foreground",
  confirmed:
    "border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]",
  completed: "border-transparent bg-muted text-muted-foreground",
};

// =============================================================================
// NFC Write Dialog — shared between Owner & Admin tag screens.
//
// Stays open during the entire write so that error messages remain visible
// even when Android shows its own NFC chooser UI on top. Uses a real
// AbortController so the user can cancel cleanly.
// =============================================================================

// Small helper: a single role login link card with copy + open actions.
export function ShareableLinkCard({ icon: Icon, title, description, href, badge, testIdPrefix }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(href);
      } else {
        const ta = document.createElement("textarea");
        ta.value = href;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success(`${title} link copied!`);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      toast.error("Couldn't copy link.");
    }
  };
  return (
    <Card className="rounded-[24px] border-border bg-card" data-testid={`${testIdPrefix}-card`}>
      <CardContent className="space-y-3 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            {Icon ? (
              <div className="rounded-xl bg-[hsl(var(--accent))]/10 p-2 text-[hsl(var(--accent))]">
                <Icon className="h-5 w-5" />
              </div>
            ) : null}
            <div>
              <p className="font-['Space_Grotesk'] text-lg font-semibold">{title}</p>
              <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
            </div>
          </div>
          {badge ? <Badge variant="outline" className="shrink-0">{badge}</Badge> : null}
        </div>
        <div className="rounded-2xl border border-border bg-secondary/60 px-3 py-2">
          <p className="break-all font-mono text-xs text-foreground" data-testid={`${testIdPrefix}-url`}>{href}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="rounded-xl"
            onClick={handleCopy}
            data-testid={`${testIdPrefix}-copy`}
          >
            <Copy className="h-4 w-4" />
            {copied ? "Copied!" : "Copy link"}
          </Button>
          <Button
            variant="outline"
            className="rounded-xl"
            asChild
          >
            <a href={href} target="_blank" rel="noreferrer noopener" data-testid={`${testIdPrefix}-open`}>
              <ScanLine className="h-4 w-4" />
              Open
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function NfcWriteDialog({ tag, onClose }) {
  const [status, setStatus] = useState("idle"); // idle | writing | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [permState, setPermState] = useState("prompt");
  const [copied, setCopied] = useState(false);
  const abortRef = useRef(null);
  const wakeLockRef = useRef(null);

  const url = useMemo(() => {
    if (!tag) return "";
    return `${window.location.origin}${tag.path || ""}`;
  }, [tag]);

  const releaseWakeLock = useCallback(async () => {
    const wl = wakeLockRef.current;
    wakeLockRef.current = null;
    if (wl) {
      try { await wl.release(); } catch (_e) { /* noop */ }
    }
  }, []);

  const acquireWakeLock = useCallback(async () => {
    try {
      if (typeof navigator !== "undefined" && navigator.wakeLock && navigator.wakeLock.request) {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      }
    } catch (_e) { /* best-effort */ }
  }, []);

  const startWrite = useCallback(async () => {
    setErrorMsg("");
    setStatus("writing");

    // Abort any prior pending write before starting a new one.
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (_e) { /* noop */ }
    }
    const controller = new AbortController();
    abortRef.current = controller;

    // Keep the screen awake while the user positions the phone over the tag.
    await acquireWakeLock();

    try {
      await writeNfcUrl(url, { signal: controller.signal });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err && err.message ? err.message : "NFC write failed.");
    } finally {
      abortRef.current = null;
      releaseWakeLock();
      try {
        const next = await getNfcPermissionState();
        setPermState(next);
      } catch (_e) { /* noop */ }
    }
  }, [url, acquireWakeLock, releaseWakeLock]);

  // Open / close lifecycle: when a tag is supplied, start the flow.
  useEffect(() => {
    if (!tag) return;
    let cancelled = false;
    setStatus("idle");
    setErrorMsg("");
    setCopied(false);
    (async () => {
      const support = describeBrowserSupport();
      if (!support.ok) {
        if (cancelled) return;
        setStatus("error");
        setErrorMsg(support.reason);
        return;
      }
      try {
        const next = await getNfcPermissionState();
        if (!cancelled) setPermState(next);
      } catch (_e) { /* noop */ }
      if (!cancelled) startWrite();
    })();
    return () => {
      cancelled = true;
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch (_e) { /* noop */ }
        abortRef.current = null;
      }
      releaseWakeLock();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tag]);

  // Auto-close 2.5s after success.
  useEffect(() => {
    if (status !== "success") return;
    const t = window.setTimeout(() => onClose(), 2500);
    return () => window.clearTimeout(t);
  }, [status, onClose]);

  const handleClose = () => {
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (_e) { /* noop */ }
      abortRef.current = null;
    }
    releaseWakeLock();
    onClose();
  };

  const handleCopyUrl = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      toast.error("Couldn't copy URL — please copy manually.");
    }
  };

  const open = Boolean(tag);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="rounded-[28px] border-border bg-background sm:max-w-md" data-testid="nfc-write-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Radio className="h-5 w-5 text-[hsl(var(--primary))]" />
            Write to NFC tag
          </DialogTitle>
          <DialogDescription>
            {status === "writing" && "Hold a blank NFC sticker flat against the back of your Android phone. Keep this tab in the foreground — don't switch apps."}
            {status === "success" && "Tag written successfully."}
            {status === "error" && "Something went wrong. See details below."}
            {status === "idle" && "Preparing…"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {tag ? (
            <div className="rounded-2xl border border-border bg-secondary/60 p-3">
              <p className="text-xs font-medium uppercase tracking-[0.24em] text-muted-foreground">URL being written</p>
              <p className="mt-1 break-all font-mono text-xs text-foreground" data-testid="nfc-write-url">{url}</p>
              <p className="mt-2 text-xs text-muted-foreground">Label: <span className="font-medium text-foreground">{tag.label}</span></p>
            </div>
          ) : null}

          <div className="rounded-2xl border border-border bg-card p-3 text-sm">
            {status === "writing" ? (
              <div className="flex items-center gap-3" data-testid="nfc-write-status-writing">
                <RefreshCw className="h-4 w-4 animate-spin text-[hsl(var(--primary))]" />
                <span>Waiting for tag… tap your phone to the sticker now.</span>
              </div>
            ) : null}
            {status === "success" ? (
              <div className="flex items-center gap-3 text-[hsl(var(--primary))]" data-testid="nfc-write-status-success">
                <CheckCircle2 className="h-4 w-4" />
                <span>Tag programmed!</span>
              </div>
            ) : null}
            {status === "error" ? (
              <div className="space-y-2 text-destructive" data-testid="nfc-write-status-error">
                <div className="flex items-start gap-3">
                  <Bell className="mt-0.5 h-4 w-4" />
                  <span className="text-sm">{errorMsg}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Permission: <span className="font-mono">{permState}</span>
                  {" · "}
                  Web NFC: <span className="font-mono">{isNfcSupported() ? "yes" : "no"}</span>
                </p>
              </div>
            ) : null}
            {status === "idle" ? (
              <div className="flex items-center gap-3 text-muted-foreground">
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span>Preparing NFC writer…</span>
              </div>
            ) : null}
          </div>

          {permState === "denied" ? (
            <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
              NFC permission is blocked for this site. Open Chrome → tap the lock icon in the address bar → Permissions → NFC devices → set to Allow → reload the page.
            </div>
          ) : null}

          {/* Manual fallback — works on every device. Useful when Samsung's
              NFC stack hands the tag to WeChat / Tags / Samsung Pay before
              Chrome's Web NFC listener can claim it. */}
          <div className="rounded-2xl border border-dashed border-border bg-secondary/30 p-3 text-xs text-muted-foreground">
            <p className="font-medium text-foreground">If your phone keeps showing "Complete action with Tags / WeChat":</p>
            <ol className="mt-2 ml-4 list-decimal space-y-1">
              <li>Tap <span className="font-medium text-foreground">Copy URL</span> below.</li>
              <li>Open <span className="font-medium text-foreground">NFC Tools</span> → Write → Add a record → URL → paste → OK → Write.</li>
              <li>This is a one-time hardware quirk on some Samsung Galaxy A devices.</li>
            </ol>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 w-full rounded-lg"
              onClick={handleCopyUrl}
              data-testid="nfc-write-copy-url"
            >
              <Copy className="h-4 w-4" />
              {copied ? "Copied!" : "Copy URL"}
            </Button>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {status === "error" || status === "writing" ? (
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={status === "writing" ? handleClose : startWrite}
              data-testid="nfc-write-secondary-button"
            >
              {status === "writing" ? "Cancel" : "Try again"}
            </Button>
          ) : null}
          <Button
            className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
            onClick={handleClose}
            data-testid="nfc-write-close-button"
          >
            {status === "success" ? "Done" : "Close"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const resizeImageFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement("canvas");
        const maxWidth = 1400;
        const maxHeight = 900;
        const scale = Math.min(maxWidth / image.width, maxHeight / image.height, 1);
        canvas.width = Math.round(image.width * scale);
        canvas.height = Math.round(image.height * scale);
        const context = canvas.getContext("2d");
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.8));
      };
      image.onerror = () => reject(new Error("Unable to process image."));
      image.src = reader.result;
    };
    reader.onerror = () => reject(new Error("Unable to read image."));
    reader.readAsDataURL(file);
  });

// ---------------------------------------------------------------------------
// TagQrPreview
//
// Renders a downloadable QR code for the URL a customer would land on after
// tapping the tag's NFC sticker. Because the scan URL (e.g. /api/menu/scan?tag=3)
// mints a fresh single-use token on EVERY visit, the QR code itself does not
// need to encode any session/expiration state — the security & expiration
// model is identical to the NFC flow. The image is generated client-side via
// the `qrcode` library, then either rendered or saved as a PNG file named
// after the tag (e.g. "qr-tag-3.png").
// ---------------------------------------------------------------------------
export function TagQrPreview({ url, filename = "qr-tag", size = 240, testIdPrefix = "tag-qr" }) {
  const [dataUrl, setDataUrl] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!url) {
      setDataUrl("");
      return;
    }
    QRCode.toDataURL(url, {
      width: 720, // generate at 3x for crisp printing
      margin: 2,
      errorCorrectionLevel: "M",
      color: { dark: "#0F0F0F", light: "#FFFFFF" },
    })
      .then((d) => { if (!cancelled) { setDataUrl(d); setError(""); } })
      .catch((e) => { if (!cancelled) setError(e?.message || "Unable to generate QR code"); });
    return () => { cancelled = true; };
  }, [url]);

  const handleDownload = () => {
    if (!dataUrl) return;
    // Pick a safe-ish filename derived from the tag label/code.
    const safe = String(filename).toLowerCase().replace(/[^a-z0-9-_]+/g, "-").replace(/^-+|-+$/g, "") || "qr-tag";
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `${safe}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="flex flex-col items-center gap-2 rounded-2xl border border-border bg-background p-3" data-testid={`${testIdPrefix}-container`}>
      <div className="flex items-center justify-center rounded-xl bg-white p-2" style={{ width: size, height: size }}>
        {dataUrl ? (
          <img src={dataUrl} alt={`QR code for ${filename}`} className="h-full w-full" style={{ imageRendering: "pixelated" }} data-testid={`${testIdPrefix}-image`} />
        ) : error ? (
          <p className="text-center text-xs text-destructive">{error}</p>
        ) : (
          <Skeleton className="h-full w-full" />
        )}
      </div>
      <Button
        variant="outline"
        size="sm"
        className="w-full rounded-lg"
        onClick={handleDownload}
        disabled={!dataUrl}
        data-testid={`${testIdPrefix}-download-button`}
      >
        <Download className="h-4 w-4" />
        Save QR image
      </Button>
    </div>
  );
}


const calculateLineTotal = (item, quantity) => {
  const price = Number(item.price || 0);
  const vatPercent = Number(item.vat_percent || 0);
  const lineSubtotal = price * quantity;
  const vatAmount = (lineSubtotal * vatPercent) / 100;
  return {
    lineSubtotal,
    vatAmount,
    lineTotal: lineSubtotal + vatAmount,
  };
};

// Compute pricing for the sides chosen on a given cart line. The first
// `free_count` selections are free; the remainder are charged at their own
// library price. Mirrors the backend logic exactly so the frontend can show
// an accurate live total before the order is placed.
export const computeSidesPricingForLine = (item, sideIds, sidesLibrary) => {
  const cfg = item?.sides_config || { selection_mode: "none", free_count: 0 };
  if (!cfg || cfg.selection_mode === "none" || !Array.isArray(sideIds) || sideIds.length === 0) {
    return { extras_per_unit: 0, snapshots: [] };
  }
  const libMap = {};
  (sidesLibrary || []).forEach((s) => { if (s && s.id) libMap[s.id] = s; });
  const free = Math.max(0, Number(cfg.free_count) || 0);
  let extrasPerUnit = 0;
  const snapshots = sideIds.map((id, idx) => {
    const side = libMap[id];
    if (!side) return null;
    const price = Number(side.price || 0);
    const charged = idx >= free;
    if (charged) extrasPerUnit += price;
    return { id, name: side.name, price, charged };
  }).filter(Boolean);
  return { extras_per_unit: extrasPerUnit, snapshots };
};

export function StatusBadge({ status, dataTestId }) {
  return (
    <Badge
      variant={status === "confirmed" ? "outline" : "secondary"}
      className={`status-badge capitalize ${statusClassName[status] || ""}`}
      data-testid={dataTestId}
    >
      {status}
    </Badge>
  );
}

export function SectionHeader({ eyebrow, title, description, action }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div className="space-y-1">
        {eyebrow ? <p className="text-sm font-medium text-[hsl(var(--primary))]">{eyebrow}</p> : null}
        <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">{title}</h2>
        {description ? <p className="max-w-2xl text-sm text-muted-foreground sm:text-base">{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <Card className="border-dashed bg-card/90">
      <CardContent className="flex flex-col items-center gap-4 px-6 py-10 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary text-[hsl(var(--primary))]">
          <Icon className="h-6 w-6" />
        </div>
        <div className="space-y-1.5">
          <h3 className="text-lg font-semibold">{title}</h3>
          <p className="mx-auto max-w-md text-sm text-muted-foreground">{description}</p>
        </div>
        {action}
      </CardContent>
    </Card>
  );
}

// Session 70 — SPLIT BILL · WHO PAID WHAT. One table, one row per guest:
// what they ordered, their share of the bill, what they actually paid
// (method + tenders), their tip, who took the payment. Used by the waiter's
// "My tips", the owner's Tips drill-down and the Operations receipt.
const splitMethodTone = (m) => {
  const k = String(m || "").toLowerCase();
  if (k === "cash") return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30";
  if (k === "card") return "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30";
  if (k === "eft") return "bg-teal-500/15 text-teal-700 dark:text-teal-300 border-teal-500/30";
  if (k === "split" || k === "multi") return "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30";
  return "bg-secondary text-muted-foreground border-border";
};

export function SplitGuestBreakdown({ breakdown, title, myTipBySplit = null, testId = "split-guest-breakdown" }) {
  if (!breakdown || !Array.isArray(breakdown.guests) || breakdown.guests.length === 0) return null;
  const guests = breakdown.guests;
  return (
    <div className="rounded-2xl border border-violet-500/30 bg-violet-500/5 p-3 sm:p-4" data-testid={testId}>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300">
            {title || `Table ${breakdown.table_number || "—"} · split bill`}
          </p>
          <p className="text-sm text-muted-foreground" data-testid={`${testId}-summary`}>
            {breakdown.guest_count} guest{breakdown.guest_count === 1 ? "" : "s"}
            {breakdown.settled_together
              ? ` · settled with one table payment${breakdown.table_payment?.payment_method ? ` (${breakdown.table_payment.payment_method})` : ""}${breakdown.table_payment?.paid_by ? ` · by ${breakdown.table_payment.paid_by}` : ""}`
              : ` · ${breakdown.paid_count} paid${breakdown.guest_count - breakdown.paid_count > 0 ? ` · ${breakdown.guest_count - breakdown.paid_count} still owing${breakdown.table_status === "closed" ? " (table closed unpaid)" : ""}` : ""}`}
          </p>
          {breakdown.settled_together && Number(breakdown.table_tips) > 0 ? (
            <p className="text-xs text-amber-700 dark:text-amber-300" data-testid={`${testId}-table-tip`}>
              Tip {formatPrice(breakdown.table_tips)} left on the table payment (not split per guest)
            </p>
          ) : null}
        </div>
        <div className="flex gap-3 text-right text-xs">
          <div><p className="text-muted-foreground">Bill</p><p className="font-semibold tabular-nums">{formatPrice(breakdown.total || 0)}</p></div>
          <div><p className="text-muted-foreground">Paid</p><p className="font-semibold tabular-nums">{formatPrice(breakdown.paid || 0)}</p></div>
          <div><p className="text-muted-foreground">Tips</p><p className="font-semibold tabular-nums text-amber-700 dark:text-amber-300">{formatPrice(breakdown.tips || 0)}</p></div>
        </div>
      </div>
      <ul className="divide-y divide-border rounded-xl border border-border bg-card">
        {guests.map((g, gi) => {
          const myTip = myTipBySplit ? myTipBySplit[g.split_id] : null;
          return (
            <li key={g.split_id || gi} className={`flex flex-wrap gap-x-4 gap-y-2 px-3 py-2.5 ${g.is_paid ? "" : "bg-rose-500/5"}`} data-testid={`${testId}-guest-${gi}`}>
              <div className="w-24 shrink-0">
                <p className="truncate font-semibold" data-testid={`${testId}-guest-${gi}-name`}>{g.name}</p>
                {g.is_paid ? (
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">{g.settled_by_table ? "Paid · table" : "Paid"}</span>
                ) : (
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-rose-700 dark:text-rose-300">Owes {formatPrice(g.outstanding || g.total || 0)}</span>
                )}
                {g.paid_by ? <p className="mt-0.5 truncate text-[10px] text-muted-foreground" title={g.paid_by}>{g.paid_by}</p> : null}
                {g.paid_at ? <p className="text-[10px] text-muted-foreground">{new Date(g.paid_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</p> : null}
              </div>
              <ul className="min-w-[140px] flex-1 space-y-0.5 text-xs" data-testid={`${testId}-guest-${gi}-items`}>
                {(g.items || []).length ? g.items.map((it, ii) => (
                  <li key={`${it.order_id}-${ii}`} className="flex justify-between gap-2">
                    <span className="truncate">{it.quantity}× {it.name}</span>
                    <span className="shrink-0 tabular-nums text-muted-foreground">{formatPrice(it.line_total || 0)}</span>
                  </li>
                )) : <li className="text-muted-foreground">No items</li>}
              </ul>
              <div className="w-44 shrink-0 space-y-0.5 text-xs">
                <div className="flex justify-between gap-2"><span className="text-muted-foreground">Share{Number(g.service_fee) > 0 ? <span className="block text-[10px]">incl. fee {formatPrice(g.service_fee)}</span> : null}</span><span className="whitespace-nowrap font-medium tabular-nums" data-testid={`${testId}-guest-${gi}-share`}>{formatPrice(g.total || 0)}</span></div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">Paid</span>
                  <span className="flex items-center gap-1.5">
                    {g.payment_method ? <Badge className={`h-4 px-1.5 text-[9px] capitalize ${splitMethodTone(g.payment_method)}`}>{g.payment_method}</Badge> : null}
                    <span className="whitespace-nowrap font-semibold tabular-nums" data-testid={`${testId}-guest-${gi}-paid`}>{formatPrice(g.paid || 0)}</span>
                  </span>
                </div>
                {Array.isArray(g.tenders) && g.tenders.length > 1 ? (
                  <ul className="space-y-0.5 pl-2 text-[10px] text-muted-foreground">
                    {g.tenders.map((t, ti) => (
                      <li key={t.id || `${t.method}-${t.amount}-${ti}`} className="flex justify-between gap-2"><span className="truncate">{t.label || t.method}</span><span className="tabular-nums">{formatPrice(t.amount || 0)}{Number(t.tip) > 0 ? ` +${formatPrice(t.tip)} tip` : ""}</span></li>
                    ))}
                  </ul>
                ) : null}
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">Tip</span>
                  <span className={`font-semibold tabular-nums ${Number(g.tip) > 0 ? "text-amber-700 dark:text-amber-300" : "text-muted-foreground"}`} data-testid={`${testId}-guest-${gi}-tip`}>
                    {g.settled_by_table && !(Number(g.tip) > 0) ? "on table bill" : formatPrice(g.tip || 0)}{myTip != null && Math.abs(Number(myTip) - Number(g.tip || 0)) > 0.009 ? <span className="ml-1 font-normal text-muted-foreground">(yours {formatPrice(myTip)})</span> : null}
                  </span>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Split the rows of a Tips drill-down into (a) split-bill TABLES — one card
// per table listing every guest — and (b) the remaining plain order rows.
// `myTipBySplit` collects the caller's own credited tip per guest so a
// waiter sees "yours" when the table's tip went to several waiters.
export const groupTipRowsBySplitTable = (rows) => {
  const tables = new Map();
  const plain = [];
  (rows || []).forEach((o) => {
    const bd = o && o.split_guests;
    if (!bd || !Array.isArray(bd.guests) || !bd.guests.length) { plain.push(o); return; }
    const key = bd.session_id || o.table_session_id || o.id;
    if (!tables.has(key)) tables.set(key, { key, breakdown: bd, rows: [], myTip: 0, myTipBySplit: {} });
    const t = tables.get(key);
    t.rows.push(o);
    const tip = Number(o.tip_amount || 0);
    t.myTip += tip;
    if (o.split_label) {
      const g = bd.guests.find((x) => x.name === o.split_label);
      if (g) t.myTipBySplit[g.split_id] = (t.myTipBySplit[g.split_id] || 0) + tip;
    }
  });
  return { tables: Array.from(tables.values()), plain };
};

export function MenuLoadingState() {
  return (
    <div className="space-y-4 py-4">
      <Skeleton className="h-24 rounded-3xl" />
      <div className="flex gap-2 overflow-hidden">
        <Skeleton className="h-10 w-24 rounded-full" />
        <Skeleton className="h-10 w-28 rounded-full" />
        <Skeleton className="h-10 w-20 rounded-full" />
      </div>
      <div className="space-y-3">
        {[1, 2, 3, 4].map((item) => (
          <Skeleton key={item} className="h-28 rounded-2xl" />
        ))}
      </div>
    </div>
  );
}

export function DashboardLoadingState() {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-3">
        {[1, 2, 3].map((item) => (
          <Skeleton key={item} className="h-28 rounded-2xl" />
        ))}
      </div>
      <Skeleton className="h-12 rounded-xl" />
      <div className="space-y-3">
        {[1, 2, 3].map((item) => (
          <Skeleton key={item} className="h-36 rounded-2xl" />
        ))}
      </div>
    </div>
  );
}

// Auto-logout when the dashboard tab leaves the foreground for too long.
// Why: staff tablets are meant to stay on the dashboard during shift —
// pressing the home button / switching apps should end the session. A
// brief grace window lets the user lock and unlock the device without
// losing their session.
//
// Implementation: subscribe to `visibilitychange` (fires on home button,
// app switch, AND screen lock). If the page stays hidden longer than
// `graceMs`, invoke the supplied logout handler. If the user returns
// before the grace expires, cancel the pending logout.
export function useAutoLogoutOnHidden(_logoutFn, _opts = {}) {
  // Session 27.98 — DISABLED. Previously this hook logged staff out
  // after 60 s of the tab/window being hidden (device sleep, phone
  // call, alt-tab). In practice this caused constant "kicked out"
  // complaints during normal service — a waiter puts the tablet
  // face-down for a minute to answer a customer, comes back to a
  // login screen. Removed per operator direction. Kept as a no-op
  // stub so the three call sites don't need code changes.
  return undefined;
}
